import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import {
   Modal,
   ModalOverlay,
   ModalContent,
   ModalHeader,
   ModalCloseButton,
   ModalBody,
   ModalFooter,
   Flex,
   Box,
   Button,
   Table,
   Thead,
   Tbody,
   Tr,
   Th,
   Td,
   TableContainer,
   Tfoot,
   Text,
   Badge,
   Divider,
} from '@chakra-ui/react';
import InputField from 'components/ui/InputField';

const TakaRow = React.memo(({ item, idx, inputRefs, handleOnChange, handleKeyDown }) => {
   return (
      <Tr key={item.id} _hover={{ bg: 'gray.50' }} transition="background-color 0.2s">
         <Td isNumeric borderColor="gray.200">
            <InputField
               ref={(el) => (inputRefs.current[`${idx}-usedMeter`] = el)}
               type="number" name="usedMeter" size="sm"
               onWheel={(e) => e.target.blur()}
               textAlign="right"
               value={item.usedMeter}
               onChange={(e) => {
                  if (e.target.value > item.pendingQty) {
                     alert("Used meter cannot be greater than Pending Qty");
                  } else {
                     handleOnChange(e, idx);
                  }
               }}
               // disabled 
               bg="gray.100"
               fontWeight="semibold"
            />
         </Td>
         <Td isNumeric borderColor="gray.200">
            <InputField
               ref={(el) => (inputRefs.current[`${idx}-mts`] = el)}
               type="number" name="mts" size="sm"
               onWheel={(e) => e.target.blur()}
               textAlign="right" value={item.mts || ''}
               onChange={(e) => handleOnChange(e, idx)}
               onKeyDown={(e) => handleKeyDown(e, idx, 'mts')}
               focusBorderColor="blue.400"
            />
         </Td>
         <Td borderColor="gray.200">
            <InputField
               ref={(el) => (inputRefs.current[`${idx}-remarks`] = el)}
               type="text" name="remarks" size="sm"
               value={item.remarks}
               onChange={(e) => handleOnChange(e, idx)}
               onKeyDown={(e) => handleKeyDown(e, idx, 'remarks')}
               placeholder="Add remarks..."
               focusBorderColor="blue.400"
            />
         </Td>
         <Td isNumeric borderColor="gray.200">
            <InputField
               type="number" name="shortMts" size="sm"
               onWheel={(e) => e.target.blur()}
               textAlign="right" value={item.shortMts}
               onChange={(e) => handleOnChange(e, idx)}
               disabled bg="orange.50" fontWeight="semibold"
               color={item.shortMts > 0 ? 'orange.600' : 'green.600'}
            />
         </Td>
         <Td isNumeric borderColor="gray.200">
            <InputField
               type="number" name="shortPerc" size="sm"
               onWheel={(e) => e.target.blur()}
               textAlign="right" value={item.shortPerc}
               onChange={(e) => handleOnChange(e, idx)}
               disabled bg="orange.50" fontWeight="semibold"
               color={item.shortPerc > 0 ? 'orange.600' : 'green.600'}
            />
         </Td>
      </Tr>
   );
});

const SelectTakaDetail = ({
   isOpen,
   onClose,
   onCloseComplete,
   takaDetails = [],
   handleSaveTakaDetail,
   selectedTakaDetails = [],
   lineItem,
   currentIndex,
}) => {
   const [rows, setRows] = useState([]);
   const inputRefs = useRef({});
   const saveBtnRefs = useRef({});
   const takaDetailsWithIds = useMemo(() => {

      // const selectedIds = new Set(
      //    lineItem.filter((_, idx) => idx !== currentIndex)
      //       ?.flatMap(x => x?.takaDetails)
      //       ?.map(t => t?.issueLineDetailId)
      //       ?.filter(Boolean)
      // )

      // const currentTakas = lineItem.filter((_, idx) => idx === currentIndex)
      //    ?.flatMap(x => x?.takaDetails)
      //    ?.map(t => t?.issueLineDetailId)
      //    ?.filter(Boolean)

      // const filteredPendingList = takaDetails.filter(
      //    p => !selectedIds?.has(p?.issue_line_detail_id)
      // );

      return takaDetails.map((item) => ({
         ...item,
         id: item.id,
      }));
   }, [takaDetails, lineItem, currentIndex]);

   const rowTotal = useMemo(() => {
      const usedMeter = rows.reduce((acc, item) => {
         return acc + Number(item.usedMeter);
      }, 0);

      const mts = rows.reduce((acc, item) => {
         return acc + Number(item.mts);
      }, 0);

      const shortMts = rows.reduce((acc, item) => {
         return acc + Number(item.shortMts);
      }, 0);
      const shortPerc = rows
         .reduce((acc, item) => {
            return acc + Number(item.shortPerc);
         }, 0)
         .toFixed(2);

      return { usedMeter, mts, shortMts, shortPerc };
   }, [rows]);

   const handleOnClose = useCallback(() => {
      onClose();
   }, [onClose]);


   const handleOnChange = useCallback((e, idx) => {
      const { name, value } = e.target;

      setRows(prev => {
         const updatedRows = [...prev];
         const row = { ...updatedRows[idx] };

         if (name === 'remarks') {
            row[name] = value;
         } else {
            const numValue = Number(value);
            row[name] = numValue;

            if (name === 'mts' || name === 'usedMeter') {
               const usedMeter = name === 'usedMeter' ? numValue : Number(row.usedMeter ?? 0);
               const mts = name === 'mts' ? numValue : Number(row.mts ?? 0);
               row.shortMts = usedMeter - mts;
               row.shortPerc = usedMeter !== 0
                  ? ((row.shortMts / usedMeter) * 100).toFixed(2)
                  : 0;
            }
         }

         updatedRows[idx] = row;
         return updatedRows;
      });
   }, []);

   const getNextInput = useCallback(
      (currentRowIdx, currentField) => {
         const fields = ['mts', 'remarks'];
         const currentFieldIdx = fields.indexOf(currentField);

         if (currentFieldIdx < fields.length - 1) {
            return {
               rowIdx: currentRowIdx,
               field: fields[currentFieldIdx + 1],
            };
         }

         if (currentRowIdx < rows.length - 1) {
            return {
               rowIdx: currentRowIdx + 1,
               field: fields[0],
            };
         }

         return null;
      },
      [rows.length],
   );

   const getPreviousInput = useCallback((currentRowIdx, currentField) => {
      const fields = ['mts', 'remarks'];
      const currentFieldIdx = fields.indexOf(currentField);

      if (currentFieldIdx > 0) {
         return {
            rowIdx: currentRowIdx,
            field: fields[currentFieldIdx - 1],
         };
      }

      if (currentRowIdx > 0) {
         return {
            rowIdx: currentRowIdx - 1,
            field: fields[fields.length - 1],
         };
      }

      return null;
   }, []);

   const handleKeyDown = useCallback(
      (e, rowIdx, fieldName) => {
         if (e.key === 'Enter' || e.key === 'Tab') {
            e.preventDefault();

            let nextInput;
            if (e.shiftKey && e.key === 'Tab') {
               nextInput = getPreviousInput(rowIdx, fieldName);
            } else {
               nextInput = getNextInput(rowIdx, fieldName);
            }

            if (nextInput) {
               const inputKey = `${nextInput.rowIdx}-${nextInput.field}`;
               const inputElement = inputRefs.current[inputKey];
               if (inputElement) {
                  inputElement.focus();
                  inputElement.select();
               }
            } else {
               if (saveBtnRefs.current) saveBtnRefs.current.focus();
            }
         }
      },
      [getNextInput, getPreviousInput],
   );

   const handleSave = useCallback(() => {
      handleSaveTakaDetail(rows);
      handleOnClose();
      setRows([]);
   }, [handleOnClose, handleSaveTakaDetail, rows]);

   useEffect(() => {
      if (isOpen) {
         const rowData = takaDetailsWithIds.map((item) => {

            // Calculate values for the row
            const usedMeter = Number(item.pendingQty3 ?? item.RQuantity3 ?? item.usedMeter ?? 0);
            const mts = Number(item.mts ?? 0);
            const shortMts = usedMeter - mts;
            const shortPerc = usedMeter > 0
               ? ((shortMts / usedMeter) * 100).toFixed(2)
               : 0;
            return {
               id: item.id,
               pendingQty: item.pendingQty3 ?? item.maxQty3,
               usedMeter: usedMeter,
               mts: mts,
               remarks: item.remarks || '',
               shortMts: shortMts,
               shortPerc: shortPerc,
               issueLineDetailId: item.issueLineDetailId,
            };
         });

         setRows(rowData);
      }
   }, [isOpen, takaDetailsWithIds, selectedTakaDetails]);

   useEffect(() => {
      if (isOpen && rows.length > 0) {
         const timer = setTimeout(() => {
            const firstMtsInput = inputRefs.current['0-mts'];
            if (firstMtsInput) {
               firstMtsInput.focus();
               // firstMtsInput.select();
            }
         }, 100);

         return () => clearTimeout(timer);
      }

      return () => { };
   }, [isOpen, rows.length]);

   return (
      <Modal isOpen={isOpen} onClose={handleOnClose} isCentered onCloseComplete={onCloseComplete} size="4xl">
         <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
         <ModalContent bg="white" color="gray.800" borderRadius="16px" boxShadow="2xl">
            <ModalHeader bg="blue.50" borderTopRadius="16px" py={4}>
               <Flex justifyContent="space-between" alignItems="center">
                  <Flex alignItems="center" gap={3}>
                     <Box w="4px" h="24px" bg="blue.500" borderRadius="full" />
                     <Box>
                        <Text fontSize="xl" fontWeight="bold" color="blue.700">
                           Taka Detail Entry
                        </Text>
                        <Text fontSize="sm" fontWeight="normal" color="gray.600" mt={1}>
                           Enter measurements and remarks for each item
                        </Text>
                     </Box>
                  </Flex>
                  <Badge colorScheme="blue" fontSize="md" px={3} py={1} borderRadius="md">
                     {rows.length} Items
                  </Badge>
               </Flex>
            </ModalHeader>
            <ModalCloseButton top={1} right={1} size="lg" borderRadius="full" _hover={{ bg: 'gray.200' }} />

            <Divider />

            <ModalBody p={4}>
               <Box
                  maxH="500px"
                  overflowY="auto"
                  borderRadius="12px"
                  border="1px solid"
                  borderColor="gray.200"
                  sx={{
                     '&::-webkit-scrollbar': {
                        width: '8px',
                     },
                     '&::-webkit-scrollbar-track': {
                        background: 'gray.100',
                        borderRadius: '4px',
                     },
                     '&::-webkit-scrollbar-thumb': {
                        background: 'gray.400',
                        borderRadius: '4px',
                     },
                     '&::-webkit-scrollbar-thumb:hover': {
                        background: 'gray.500',
                     },
                  }}>
                  <TableContainer>
                     <Table variant="simple" size="md" width="100%">
                        <Thead
                           position="sticky"
                           top={0}
                           bg="blue.50"
                           zIndex={1}
                           sx={{
                              th: {
                                 borderBottom: '2px solid',
                                 borderColor: 'gray.300',
                                 textTransform: 'uppercase',
                                 letterSpacing: '0.5px',
                                 fontSize: 'xs',
                                 fontWeight: 'bold',
                                 color: 'blue.700',
                                 py: 4,
                              },
                           }}>
                           <Tr>
                              <Th isNumeric minW="120px">
                                 Used Meter
                              </Th>
                              <Th isNumeric minW="120px">
                                 MTS
                              </Th>
                              <Th minW="200px">Remarks</Th>
                              <Th isNumeric minW="120px">
                                 Short MTS
                              </Th>
                              <Th isNumeric minW="120px">
                                 Short %
                              </Th>
                           </Tr>
                        </Thead>
                        <Tbody>
                           {rows.map((item, idx) => (
                              <TakaRow
                                 key={item.id}
                                 item={item}
                                 idx={idx}
                                 inputRefs={inputRefs}
                                 handleOnChange={handleOnChange}
                                 handleKeyDown={handleKeyDown}
                              />
                           ))}
                        </Tbody>
                        <Tfoot
                           position="sticky"
                           bottom={0}
                           bg="gray.50"
                           zIndex={1}
                           sx={{
                              th: {
                                 borderTop: '2px solid',
                                 borderColor: 'gray.300',
                                 fontSize: 'sm',
                                 fontWeight: 'bold',
                                 py: 4,
                              },
                           }}>
                           <Tr>
                              <Th isNumeric color="blue.700">
                                 {rowTotal.usedMeter.toFixed(2)}
                              </Th>
                              <Th isNumeric color="blue.700">
                                 {rowTotal.mts.toFixed(2)}
                              </Th>
                              <Th color="gray.500">Total</Th>
                              <Th isNumeric color={rowTotal.shortMts > 0 ? 'orange.600' : 'green.600'}>
                                 {rowTotal.shortMts.toFixed(2)}
                              </Th>
                              <Th isNumeric color={rowTotal.shortPerc > 0 ? 'orange.600' : 'green.600'}>
                                 {((rowTotal.mts.toFixed(2) * 100) / rowTotal.usedMeter.toFixed(2)).toFixed(2)}%
                              </Th>
                              {/* <Th isNumeric color={rowTotal.shortPerc > 0 ? 'orange.600' : 'green.600'}>
                                 {rowTotal.shortPerc}%
                              </Th> */}
                           </Tr>
                        </Tfoot>
                     </Table>
                  </TableContainer>
               </Box>
            </ModalBody>

            <Divider />

            <ModalFooter bg="gray.50" borderBottomRadius="16px" p={6}>
               <Flex gap={3}>
                  <Button
                     ref={saveBtnRefs}
                     colorScheme="blue"
                     flex="1"
                     size="lg"
                     _focus={{ outlineColor: 'blue.500' }}
                     onClick={handleSave}
                     boxShadow="md"
                     _hover={{
                        transform: 'translateY(-2px)',
                        boxShadow: 'lg',
                     }}
                     transition="all 0.2s">
                     Save Details
                  </Button>
                  <Button
                     colorScheme="red"
                     variant="outline"
                     onClick={() => handleOnClose()}
                     flex="1"
                     size="lg"
                     leftIcon={
                        <Box as="span" fontSize="xl">
                           ✕
                        </Box>
                     }
                     _hover={{
                        bg: 'red.50',
                        transform: 'translateY(-2px)',
                        boxShadow: 'lg',
                     }}
                     transition="all 0.2s">
                     Cancel
                  </Button>
               </Flex>
            </ModalFooter>
         </ModalContent>
      </Modal>
   );
};

export default SelectTakaDetail;
